/**
 * Shared style injection for the group surfaces.
 *
 * design.md §2 — hairlines over shadows; one accent (reserved for the primary
 *   CTA + save-state signals); warm neutrals via tokens; quiet 200ms motion.
 * design.md §4 — Geist for prose, Geist Mono + tabular-nums for quantities
 *   (member counts, relative timestamps).
 * Rule 7 — 44px min tap targets; mobile-first layout.
 */

const GROUP_STYLE_ID = "proof-groups-css";

export const GROUP_CSS = `
  /* ── Page shell ─────────────────────────────────────────────────────────── */
  .proof-group-page {
    min-height: 100dvh;
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-sans);
    display: flex;
    flex-direction: column;
  }
  .proof-group-page__inner {
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
    padding: var(--space-6) var(--space-5) var(--space-12);
    box-sizing: border-box;
  }

  /* ── Back / top row ─────────────────────────────────────────────────────── */
  .proof-group-back {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    min-height: 44px;
    padding: 0 var(--space-2) 0 0;
    background: none;
    border: none;
    color: var(--muted);
    font-family: var(--font-sans);
    font-size: 13px;
    cursor: pointer;
    transition: color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }
  .proof-group-back:hover { color: var(--text); }

  /* ── Group header ───────────────────────────────────────────────────────── */
  /* flex-wrap is load-bearing: the actions cluster is flex-shrink:0, so without
     wrapping it claims its full single-line width on a 375px row and squeezes
     the title wrapper (min-width:0) to ~1ch — which, with a mid-word-breaking
     title, renders one glyph per line. Wrapping drops the actions to their own
     line below the title on narrow viewports instead (Rule 7). */
  .proof-group-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-4);
    margin-top: var(--space-2);
  }
  /* Wrap at word boundaries (break-word), never shatter mid-word — matches the
     recipe header's FitTitle intent (design.md §4: Inter prose, break at spaces).
     break-word also keeps the title's min-content = its longest word, so a
     squeezed flex slot can't collapse the intrinsic size to a single character. */
  .proof-group-title {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    margin: 0;
    min-width: 0;
    overflow-wrap: break-word;
  }
  /* Prose line under the title ("N members" / "by @handle · shared 3d").
     Sans for the words; numerics wrap in .proof-group-mono (design.md §4 —
     mono is for data only, never prose). */
  .proof-group-sub {
    margin-top: var(--space-1);
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
  }

  /* ── Read-only state line (downgrade safety, WP-B) ──────────────────────── */
  /* A quiet statement of fact under the header, not an alert: hairline rules
     above and below, no fill, no accent, no icon (design.md §2 — hairlines over
     shadows, the accent is reserved for signal surfaces). Sans, because it is
     prose (design.md §4 — mono is for data only). */
  .proof-group-state {
    margin-top: var(--space-4);
    padding: var(--space-2) 0;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
    font-family: var(--font-sans);
    font-size: 13px;
    line-height: 1.5;
    color: var(--muted);
  }

  /* Trailing per-row marker in a group list ("Read-only"). Same faint meta
     treatment as the row's member count; flex-shrink:0 so it survives a 375px
     row without wrapping under the name. */
  .proof-group-row__flag {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
    flex-shrink: 0;
  }

  /* Data-only numerics inside prose lines (counts, relative times) —
     design.md §4: Geist Mono + tabular-nums for quantities. */
  .proof-group-mono {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }

  /* ── Action buttons (Invite / Members / owner controls) ─────────────────── */
  /* max-width is load-bearing at 375px: when the header wraps the actions to
     their own line, this flex-shrink:0 cluster would otherwise claim its full
     max-content width (~408px for the 5 owner buttons) and overflow a 375px
     viewport — the Delete button ended up ~53px off-screen and unreachable by
     any scroll. Capping the cluster at 100% of the row lets its own flex-wrap
     drop overflowing buttons onto a second line instead (Rule 7). Desktop is
     unaffected: 408px < the 720px content column, so the cap never clamps. */
  .proof-group-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    flex-shrink: 0;
    max-width: 100%;
  }
  .proof-group-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-1);
    min-height: 44px;
    padding: 0 var(--space-3);
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-button, 8px);
    cursor: pointer;
    transition: border-color var(--motion-state, 200ms ease), color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
    white-space: nowrap;
  }
  .proof-group-btn:hover { border-color: color-mix(in srgb, var(--text) 20%, var(--line)); }
  .proof-group-btn:disabled { color: var(--faint); cursor: not-allowed; }
  /* Primary CTA — the only surface that may carry the permitted 1px shadow. */
  .proof-group-btn--primary {
    /* Fallback is var(--bg) (a warm near-white token), never literal #fff —
       design.md §4 forbids pure white. Matches the proof-group-check fallback. */
    color: var(--on-accent, var(--bg));
    background: var(--accent);
    border-color: var(--accent);
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  }
  .proof-group-btn--primary:hover {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--text) 8%, var(--accent));
  }
  /* A disabled primary DROPS the accent entirely (design.md §2 — the accent is
     reserved for live signal surfaces; an accent-filled control the user cannot
     press reads as the loudest thing on the page while doing nothing). It falls
     back to the plain hairline button, muted, with the permitted shadow removed.
     Load-bearing for the read-only group page, where Invite is disabled. */
  .proof-group-btn--primary:disabled,
  .proof-group-btn--primary:disabled:hover {
    background: var(--bg);
    border-color: var(--line);
    box-shadow: none;
  }
  .proof-group-btn--danger { color: var(--del, #c03a24); }
  .proof-group-btn--danger:hover { border-color: var(--del, #c03a24); }

  /* ── Section headings ───────────────────────────────────────────────────── */
  .proof-group-section {
    margin-top: var(--space-8);
  }
  /* design.md §4.2 section-label treatment — matches the canonical Library
     index slab (LibraryCollectionsIndex): Geist Mono 11px, uppercase, 0.14em
     tracking, followed by a hairline rule so the page reads as one typographic
     system (library-surface redesign §C1 "Groups section — same quiet
     treatment"). */
  .proof-group-section__heading {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
    margin: 0 0 var(--space-3);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--line);
  }

  /* ── Share cards ────────────────────────────────────────────────────────── */
  .proof-group-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-2);
  }
  @media (min-width: 620px) {
    .proof-group-cards { grid-template-columns: 1fr 1fr; }
  }
  .proof-group-card {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: 64px;
    padding: var(--space-3);
    text-align: left;
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-card, 12px);
    cursor: pointer;
    transition: border-color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
    font-family: var(--font-sans);
    color: inherit;
  }
  .proof-group-card:hover { border-color: color-mix(in srgb, var(--text) 18%, var(--line)); }
  .proof-group-card__emoji {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    line-height: 1;
    border-radius: var(--radius-button, 8px);
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }
  .proof-group-card__text { min-width: 0; flex: 1 1 0; }
  .proof-group-card__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .proof-group-card__meta {
    margin-top: 2px;
    font-size: 12px;
    color: var(--faint);
    display: flex;
    align-items: center;
    gap: var(--space-1);
    flex-wrap: wrap;
  }
  .proof-group-card__mono {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  /* Passive rating readout — muted mono, never accent (design.md §2: the
     accent is reserved for signal surfaces; matches AttemptCard's rating). */
  .proof-group-card__rating {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    color: var(--muted);
  }

  /* ── Rows (group list, members, requests) ───────────────────────────────── */
  .proof-group-list { display: flex; flex-direction: column; gap: var(--space-2); }
  .proof-group-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: 52px;
    padding: var(--space-2) var(--space-3);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-card, 12px);
    font-family: var(--font-sans);
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }
  .proof-group-row--static { cursor: default; }
  /* A row that cannot be actioned (mid-submit, or a read-only group in the
     share picker) reads as unavailable: the name drops to muted and the hover
     hairline never lights. Dimming only — no strike-through, no lock glyph. */
  .proof-group-row:disabled { cursor: not-allowed; }
  .proof-group-row:disabled .proof-group-row__name { color: var(--muted); }
  .proof-group-row:hover:not(.proof-group-row--static):not(:disabled) {
    border-color: color-mix(in srgb, var(--text) 18%, var(--line));
  }
  .proof-group-row__text { min-width: 0; flex: 1 1 0; }
  .proof-group-row__name { font-size: 14px; font-weight: 500; color: var(--text); }
  /* Sans — meta lines carry prose ("wants to join", role words); numerics
     inside them wrap in .proof-group-mono (design.md §4). */
  .proof-group-row__meta {
    font-size: 12px; color: var(--faint);
    font-family: var(--font-sans);
  }
  .proof-group-row__actions { display: flex; gap: var(--space-2); flex-shrink: 0; }

  /* ── Quiet inline buttons (Approve/Decline/Remove/Try again) ────────────── */
  /* 44px touch floor by default (Rule 7 / WCAG 2.5.5); density step-down is
     gated on (pointer: fine) so touch tablets keep the floor at any width. */
  .proof-group-quiet {
    display: inline-flex; align-items: center; justify-content: center;
    min-height: 44px; padding: 0 var(--space-3);
    font-family: var(--font-sans); font-size: 13px; font-weight: 500;
    background: none; border: 1px solid var(--line);
    border-radius: var(--radius-button, 8px); color: var(--text);
    cursor: pointer; transition: border-color var(--motion-state, 200ms ease), color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent; white-space: nowrap;
  }
  @media (min-width: 768px) and (pointer: fine) {
    .proof-group-quiet { min-height: 40px; }
  }
  .proof-group-quiet:hover { border-color: color-mix(in srgb, var(--text) 20%, var(--line)); }
  .proof-group-quiet:disabled { color: var(--faint); cursor: not-allowed; }
  .proof-group-quiet--danger { color: var(--del, #c03a24); }
  .proof-group-quiet--danger:hover { border-color: var(--del, #c03a24); }

  /* ── Empty / state text ─────────────────────────────────────────────────── */
  .proof-group-empty {
    font-family: var(--font-sans);
    font-size: 14px;
    color: var(--muted);
    line-height: 1.6;
    padding: var(--space-4) 0;
  }
  .proof-group-loading {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    padding: var(--space-4) 0;
  }

  /* ── Centered gate (join route, load errors) ────────────────────────────── */
  .proof-group-gate {
    min-height: 100dvh;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: var(--space-4);
    padding: var(--space-8) var(--space-5);
    text-align: center;
    background: var(--bg); color: var(--text);
    font-family: var(--font-sans);
  }
  .proof-group-gate__title { font-size: 20px; font-weight: 600; margin: 0; }
  .proof-group-gate__body {
    font-size: 15px; color: var(--muted); max-width: 420px; line-height: 1.6; margin: 0;
  }
  .proof-group-gate__actions { display: flex; gap: var(--space-2); flex-wrap: wrap; justify-content: center; }
  /* Quiet secondary line under the gate actions ("Already have an account?").
     No accent — design.md §2 principle 1 reserves the accent for the primary
     CTA / signal surfaces. */
  .proof-group-gate__aside {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--faint);
    margin: 0;
  }
  .proof-group-link {
    background: none;
    border: none;
    padding: 0;
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    text-decoration: underline;
    cursor: pointer;
    transition: color var(--motion-state, 200ms ease);
    -webkit-tap-highlight-color: transparent;
  }
  .proof-group-link:hover,
  .proof-group-link:focus-visible { color: var(--text); }

  /* ── Modal (New group / Rename) ─────────────────────────────────────────── */
  .proof-group-modal-scrim {
    position: fixed; inset: 0; z-index: 300;
    background: color-mix(in srgb, var(--text) 32%, transparent);
    display: flex; align-items: flex-end; justify-content: center;
  }
  @media (min-width: 640px) {
    .proof-group-modal-scrim { align-items: center; }
  }
  .proof-group-modal {
    width: 100%; max-width: 420px;
    /* Rule 7: long rosters/lists must scroll inside the sheet, never push the
       title off-screen. dvh so iOS Safari's URL bar collapse is respected. */
    max-height: 85dvh;
    overflow-y: auto;
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-card, 12px) var(--radius-card, 12px) 0 0;
    padding: var(--space-5);
    box-sizing: border-box;
  }
  @media (min-width: 640px) {
    .proof-group-modal { border-radius: var(--radius-card, 12px); }
  }
  .proof-group-modal__title { font-size: 16px; font-weight: 600; margin: 0 0 var(--space-4); }
  .proof-group-modal__label {
    display: block; font-size: 13px; color: var(--muted); margin-bottom: var(--space-1);
  }
  .proof-group-input {
    width: 100%; box-sizing: border-box;
    min-height: 44px; padding: 0 var(--space-3);
    font-family: var(--font-sans); font-size: 15px; color: var(--text);
    background: var(--bg); border: 1px solid var(--line);
    border-radius: var(--radius-button, 8px);
    transition: border-color var(--motion-state, 200ms ease);
  }
  .proof-group-input:focus { outline: none; border-color: var(--accent); }
  .proof-group-modal__actions {
    display: flex; justify-content: flex-end; gap: var(--space-2); margin-top: var(--space-5);
  }
  .proof-group-modal__error {
    margin-top: var(--space-2); font-size: 13px; color: var(--del, #c03a24);
  }

  /* ── Invite revoke row (hairline-separated, wraps at 375px) ──────────────── */
  .proof-group-invite-revoke {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: 1px solid var(--line);
  }
  .proof-group-invite-revoke__text {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
  }

  /* ── Cook reports on the group share view (B-WP1b) ───────────────────────── */
  .proof-report-section {
    margin-top: var(--space-8);
    padding-top: var(--space-5);
    border-top: 1px solid var(--line);
  }
  .proof-report-section__title {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    margin: 0 0 var(--space-1);
  }
  /* Aggregate line — muted; the words render in Geist (prose), the counts +
     median in Geist Mono + tabular-nums (design.md §4: mono for data only).
     Never accent — a passive readout is not a signal surface (design.md §2). */
  .proof-report-aggregate {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    margin: 0 0 var(--space-4);
    line-height: 1.5;
  }
  .proof-report-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .proof-report-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 44px;
    padding: var(--space-3) 0;
    border-top: 1px solid var(--line);
  }
  .proof-report-row:first-child { border-top: none; }
  .proof-report-row__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .proof-report-row__handle {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
    color: var(--text);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Passive rating readout — muted mono, never accent (matches the share card). */
  .proof-report-row__rating {
    flex-shrink: 0;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 13px;
    color: var(--muted);
  }
  .proof-report-row__body {
    font-family: var(--font-sans);
    font-size: 14px;
    color: var(--text);
    line-height: 1.5;
    margin: 0;
    overflow-wrap: anywhere;
  }
  /* Photo slot — a quiet placeholder until Photos v1 (no <img>, no 404).
     Recessed warm-neutral tile with a functional hairline; the "Photo" label
     reads as data (mono, muted), never decoration. design.md §2 — hairlines
     over shadows, warm neutrals, no accent (a placeholder is not a signal). */
  .proof-report-row__photo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 48px;
    margin-top: 2px;
    background: var(--surface-subtle);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
  }
  .proof-report-row__photo-label {
    font-size: 11px;
    letter-spacing: 0.02em;
    color: var(--faint);
  }
  .proof-report-row__dates {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
  }
  /* Single-line date pair on tablet/desktop; the compact form on the narrowest
     viewport so the pair never overflows the row (Rule 7). */
  .proof-report-row__dates-compact { display: inline; }
  .proof-report-row__dates-full { display: none; }
  @media (min-width: 420px) {
    .proof-report-row__dates-compact { display: none; }
    .proof-report-row__dates-full { display: inline; }
  }

  /* Group share CARD — quiet secondary cook line under the meta row. */
  .proof-group-card__cooks {
    margin-top: 2px;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
  }

  /* ── Multi-select checkbox (Share-to-group sheet, B-WP14) ────────────────── */
  /* A hairline square; the checked state fills with the accent (a current-state
     signal surface — design.md §2). No shadow, 200ms ease. */
  .proof-group-check {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--bg);
    color: var(--on-accent, var(--bg));
    font-size: 13px;
    line-height: 1;
    transition: background var(--motion-state, 200ms ease), border-color var(--motion-state, 200ms ease);
  }
  .proof-group-check--on {
    background: var(--accent);
    border-color: var(--accent);
  }
  /* Post-attempt inline failure note on a share row — never semantic red beyond
     the shared --del token (design rule). */
  .proof-group-row__meta--fail { color: var(--del, #c03a24); flex-shrink: 0; }

  /* ── Notes on the group share view (B-WP2) ───────────────────────────────── */
  .proof-notes-section {
    margin-top: var(--space-8);
    padding-top: var(--space-5);
    border-top: 1px solid var(--line);
  }
  .proof-notes-section__title {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    margin: 0 0 var(--space-3);
  }
  .proof-notes-empty {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    margin: 0 0 var(--space-4);
  }
  .proof-notes-list {
    list-style: none;
    margin: 0 0 var(--space-4);
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .proof-notes-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--space-3) 0;
    border-top: 1px solid var(--line);
  }
  .proof-notes-row:first-child { border-top: none; }
  .proof-notes-row__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .proof-notes-row__handle {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 500;
    color: var(--text);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .proof-notes-row__aside {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--faint);
  }
  .proof-notes-row__body {
    font-family: var(--font-sans);
    font-size: 14px;
    color: var(--text);
    line-height: 1.5;
    margin: 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  /* Kebab menu — a quiet 44px touch target with a hairline popover. */
  .proof-notes-menu { position: relative; display: inline-flex; }
  .proof-notes-kebab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    background: none;
    border: none;
    color: var(--muted);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: color var(--motion-state, 200ms ease);
  }
  .proof-notes-kebab:hover { color: var(--text); }
  .proof-notes-kebab:disabled { color: var(--faint); cursor: not-allowed; }
  .proof-notes-menu__pop {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    min-width: 120px;
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-button, 8px);
    overflow: hidden;
  }
  .proof-notes-menu__item {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 0 var(--space-3);
    background: none;
    border: none;
    text-align: left;
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background var(--motion-state, 200ms ease);
  }
  .proof-notes-menu__item:hover { background: color-mix(in srgb, var(--text) 5%, transparent); }
  .proof-notes-menu__item--danger { color: var(--del, #c03a24); }
  /* Compose row — full-width textarea (Rule 7); the Add-note CTA carries the
     one reserved accent (a primary action — design.md §2). */
  .proof-notes-compose { display: flex; flex-direction: column; gap: var(--space-2); }
  .proof-notes-input {
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    padding: var(--space-2) var(--space-3);
    font-family: var(--font-sans);
    font-size: 15px;
    line-height: 1.5;
    color: var(--text);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-button, 8px);
    resize: vertical;
    transition: border-color var(--motion-state, 200ms ease);
  }
  .proof-notes-input:focus { outline: none; border-color: var(--accent); }
  .proof-notes-compose__foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-3);
  }
  .proof-notes-remaining {
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    color: var(--faint);
  }

  /* ── Group activity ledger strip (B-WP3) ─────────────────────────────────── */
  .proof-activity-strip {
    margin-top: var(--space-5);
    padding: var(--space-3) 0;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
  }
  .proof-activity-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .proof-activity-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    min-height: 24px;
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
  }
  .proof-activity-row__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .proof-activity-row__subject { color: var(--text); }
  .proof-activity-row__time {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-size: 12px;
    color: var(--faint);
  }
  /* Quiet "Show more" — no accent (a passive record, not a signal surface). */
  .proof-activity-more {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    margin-top: var(--space-1);
    padding: 0;
    background: none;
    border: none;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted);
    cursor: pointer;
    text-decoration: underline;
    -webkit-tap-highlight-color: transparent;
    transition: color var(--motion-state, 200ms ease);
  }
  .proof-activity-more:hover { color: var(--text); }
`;

export function injectGroupStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(GROUP_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = GROUP_STYLE_ID;
  el.textContent = GROUP_CSS;
  document.head.appendChild(el);
}
